import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Check, AlertCircle, CreditCard, Loader2, Download, ExternalLink, Info, Mail, Truck, FileText, Hash, MapPin, Printer, Package, ChevronRight } from 'lucide-react';
import { generatePackingSlipPDF, openPrintableVersion } from '../utils/pdfGenerator';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_nice-grading-app/artifacts/iks4rsh0_Nice%20logo%200925%20%283%29.jpg';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading: authLoading, checkAuth } = useAuth();
  
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [fullReviewData, setFullReviewData] = useState(null);
  const [saveForFuture] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const sessionId = searchParams.get('session_id');


  // Progress bar steps (4 logical steps) - all completed on this page
  const progressSteps = [
    { number: 1, title: 'Add Cards', icon: Hash },
    { number: 2, title: 'Shipping Info', icon: MapPin },
    { number: 3, title: 'Review Order', icon: CreditCard },
    { number: 4, title: 'Confirmation', icon: Check }
  ];

  // Send confirmation email (logged to console for now)
  const sendConfirmationEmail = useCallback((sub) => {
    const emailContent = {
      to: user?.email,
      subject: `Submission Confirmed - ${sub?.submission_number}`,
      body: `
        Dear ${user?.name || 'Customer'},

        Thank you for your submission to NICE Grading!

        SUBMISSION DETAILS:
        -------------------
        Submission Number: ${sub?.submission_number}
        Cards: ${sub?.card_count} @ $${sub?.price_per_card?.toFixed(2)}/card
        Grading Total: $${sub?.total_price?.toFixed(2)}
        Return Shipping: $15.00
        Total: $${(sub?.total_price + 15).toFixed(2)}

        NEXT STEPS:
        -----------
        1. Download and print your packing slip from your dashboard
        2. Package your cards securely using card savers or top loaders
        3. Include the packing slip inside your package
        4. Ship your cards to the address matching your carrier:
           
           FOR USPS:
           NICE Grading
           Attn: Receiving
           P.O. Box 30041
           Rochester, NY 14603
           
           FOR UPS/FedEx:
           NICE Grading
           Attn: Receiving
           250 Cumberland St., Ste. 202
           Rochester, NY 14605
           
           Reference: Submission #${sub?.submission_number}

        5. We'll email you when your cards arrive
        6. Payment will be processed when we receive your cards

        Track your submission status at: ${window.location.origin}/dashboard

        Thank you for choosing NICE Grading!
      `
    };

    console.log('📧 CONFIRMATION EMAIL:', emailContent);
    console.log('Email would be sent to:', emailContent.to);
    console.log('Subject:', emailContent.subject);
  }, [user]);

  const completeSetup = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await axios.post(`${API}/payments/complete-setup`, {
        session_id: sessionId,
        save_for_future: saveForFuture
      });

      if (response.data.success) {
        setStatus('success');
        setPaymentMethod(response.data.payment_method);
        setSubmissionId(response.data.submission_id);
        
        // Fetch submission details
        try {
          const subResponse = await axios.get(`${API}/submissions/${response.data.submission_id}/review`);
          // API returns submission data directly at root level (SubmissionWithCards model)
          setSubmission(subResponse.data);
          setFullReviewData(subResponse.data);
          
          // Send confirmation email via API
          try {
            const emailResponse = await axios.post(`${API}/email/send-confirmation`, {
              submission_id: response.data.submission_id
            });
            console.log('📧 Confirmation email result:', emailResponse.data);
          } catch (emailErr) {
            console.error('Failed to send confirmation email:', emailErr);
            // Don't block the flow if email fails
          }
        } catch (e) {
          console.error('Failed to fetch submission details:', e);
        }
        
        // Refresh user data to get updated payment method info
        if (checkAuth) {
          await checkAuth();
        }
      }
    } catch (err) {
      console.error('Failed to complete setup:', err);
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to save payment method');
    }
  }, [sessionId, saveForFuture, checkAuth]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!sessionId) {
      setStatus('error');
      setError('Invalid payment session');
      return;
    }

    // Complete the setup automatically
    completeSetup();
  }, [authLoading, isAuthenticated, sessionId, navigate, completeSetup]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <img src={LOGO_URL} alt="NICE Grading" className="h-10 w-auto" />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps - All Completed */}
        <div className="mb-10">
          <div className="flex items-center justify-between">
            {progressSteps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-500 text-white">
                      <Check className="w-6 h-6" />
                    </div>
                    <span className="mt-2 text-xs font-medium hidden sm:block text-green-600">
                      {step.title}
                    </span>
                  </div>
                  {index < progressSteps.length - 1 && (
                    <div className="flex-1 h-1 mx-2 sm:mx-4 rounded bg-green-500" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {status === 'processing' && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-[#003594]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#003594] animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Creating Your Submission</h2>
              <p className="text-gray-500">Please wait while we finalize your order...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              {/* Success Header */}
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-12 h-12 text-green-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Submission Complete!</h2>
                <p className="text-gray-500 mb-4">Your order has been successfully placed.</p>
                
                {/* Submission Number - Large and Bold */}
                <div className="bg-[#003594]/5 border-2 border-[#003594]/20 rounded-xl p-4 max-w-md mx-auto">
                  <p className="text-sm text-gray-500 mb-1">Submission Number</p>
                  <p className="text-2xl font-bold text-[#003594]">{submission?.submission_number || 'Loading...'}</p>
                  {submission && (
                    <p className="text-sm text-gray-600 mt-2">
                      {submission?.card_count} card{submission?.card_count !== 1 ? 's' : ''} @ ${submission?.price_per_card?.toFixed(2)}/card = <span className="font-semibold">${submission?.total_price?.toFixed(2)}</span>
                    </p>
                  )}
                </div>

                {/* Payment Method Saved */}
                {paymentMethod && (
                  <div className="mt-4 inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm">
                    <CreditCard className="w-4 h-4" />
                    <span>Saved: {paymentMethod.brand} ending in {paymentMethod.last4}</span>
                  </div>
                )}
              </div>

              {/* PROMINENT DOWNLOAD SECTION */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="bg-gradient-to-r from-[#003594] to-[#002554] rounded-2xl p-6 text-white shadow-lg">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-center sm:text-left">
                      <h3 className="text-xl font-bold mb-1 flex items-center justify-center sm:justify-start gap-2">
                        <FileText className="w-6 h-6" />
                        Download Your Packing Slip
                      </h3>
                      <p className="text-blue-100 text-sm">Print and include with your cards for faster processing</p>
                    </div>
                    <Button 
                      className="bg-white text-[#003594] hover:bg-gray-100 px-8 py-6 text-lg font-bold flex items-center gap-3 shadow-md min-w-[220px] justify-center"
                      onClick={async () => {
                        if (!submission || !fullReviewData) {
                          setPdfError('Submission data not available. Please refresh and try again.');
                          return;
                        }
                        setIsGeneratingPDF(true);
                        setPdfError(null);
                        try {
                          await generatePackingSlipPDF(
                            submission,
                            user,
                            fullReviewData.cards || [],
                            fullReviewData.shipping_address
                          );
                        } catch (err) {
                          console.error('Failed to generate PDF:', err);
                          setPdfError('Unable to generate PDF. Please try the printable version below.');
                        } finally {
                          setIsGeneratingPDF(false);
                        }
                      }}
                      disabled={isGeneratingPDF}
                      data-testid="download-packing-slip-btn"
                    >
                      {isGeneratingPDF ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="w-6 h-6" />
                          Download PDF
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* PDF Error Message */}
                  {pdfError && (
                    <div className="mt-4 bg-red-500/20 border border-red-300/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-white mb-2">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{pdfError}</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                        onClick={() => {
                          if (submission && fullReviewData) {
                            openPrintableVersion(
                              submission,
                              user,
                              fullReviewData.cards || [],
                              fullReviewData.shipping_address
                            );
                          }
                        }}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Open Printable Version
                      </Button>
                    </div>
                  )}
                  
                  {/* Alternative: View printable version link */}
                  <div className="mt-4 text-center">
                    <Link 
                      to={`/submissions/${submissionId}/print`}
                      target="_blank"
                      className="text-blue-100 hover:text-white text-sm underline"
                    >
                      Or view printable version in browser
                    </Link>
                  </div>
                </div>
              </div>

              <div className="max-w-2xl mx-auto space-y-6">
                {/* Next Steps Section */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#003594]" />
                    Next Steps
                  </h3>
                  <ol className="space-y-4">
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium text-gray-900">Print your packing slip (downloaded above)</p>
                        <p className="text-sm text-gray-500">The PDF includes all submission details and shipping instructions</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium text-gray-900">Include the packing slip with your cards</p>
                        <p className="text-sm text-gray-500">Place it on top of your cards inside the package</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium text-gray-900">Ship your cards to our grading lab</p>
                        <p className="text-sm text-gray-500">See shipping address below</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">4</span>
                      <div>
                        <p className="font-medium text-gray-900">We&apos;ll email you when your cards arrive</p>
                        <p className="text-sm text-gray-500">Check {user?.email} for updates</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">5</span>
                      <div>
                        <p className="font-medium text-gray-900">Payment will be processed when we receive your cards</p>
                        <p className="text-sm text-gray-500">Your card on file will be charged ${submission ? (submission.total_price + 15).toFixed(2) : '...'}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">6</span>
                      <div>
                        <p className="font-medium text-gray-900">Track your submission status in your dashboard</p>
                        <p className="text-sm text-gray-500">View real-time updates on your submission progress</p>
                      </div>
                    </li>
                  </ol>
                </div>

                {/* Shipping Addresses */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-[#003594]" />
                    Ship Your Cards To
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Choose the address that matches your shipping carrier:</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* USPS Address */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-blue-800">USPS</span>
                      </div>
                      <div className="text-gray-700 text-sm">
                        <p className="font-semibold">NICE Grading</p>
                        <p>Attn: Receiving</p>
                        <p>P.O. Box 30041</p>
                        <p>Rochester, NY 14603</p>
                      </div>
                    </div>
                    
                    {/* UPS/FedEx Address */}
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                          <Truck className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-amber-800">UPS / FedEx</span>
                      </div>
                      <div className="text-gray-700 text-sm">
                        <p className="font-semibold">NICE Grading</p>
                        <p>Attn: Receiving</p>
                        <p>250 Cumberland St.</p>
                        <p>Ste. 202</p>
                        <p>Rochester, NY 14605</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-[#003594] font-medium mt-4 text-center">
                    Reference: Submission #{submission?.submission_number}
                  </p>
                </div>

                {/* Important Reminders */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Important Reminders
                  </h3>
                  <ul className="space-y-2 text-amber-800">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Package your cards securely to prevent damage during shipping</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Include your packing slip inside the package</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>We recommend insuring your shipment for valuable cards</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Keep your tracking number for your records</span>
                    </li>
                  </ul>
                </div>

                {/* Shipping Instructions Link */}
                <div className="bg-gradient-to-r from-[#003594] to-blue-600 rounded-lg p-5 text-white">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1">Need packing help?</h3>
                      <p className="text-blue-100 text-sm mb-3">
                        View our detailed shipping instructions with step-by-step packing guide, best practices, and tips for protecting your cards in transit.
                      </p>
                      <Link 
                        to="/shipping-instructions" 
                        className="inline-flex items-center gap-2 bg-white text-[#003594] px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors text-sm"
                        data-testid="shipping-instructions-link"
                      >
                        View Shipping Instructions
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Link to="/dashboard">
                    <Button variant="outline" className="w-full sm:w-auto px-8 py-6 flex items-center justify-center gap-2 border-2">
                      <ExternalLink className="w-5 h-5" />
                      View Dashboard
                    </Button>
                  </Link>
                </div>

                {/* Start New Submission Link */}
                <div className="text-center pt-2">
                  <Link to="/new-submission" className="text-[#003594] hover:underline text-sm font-medium">
                    Start a New Submission
                  </Link>
                </div>

                {/* Email Confirmation Notice */}
                <div className="text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>A confirmation email has been sent to {user?.email}</span>
                </div>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Setup Failed</h2>
              <p className="text-red-600 mb-8">{error || 'Something went wrong. Please try again.'}</p>

              <div className="flex gap-4 justify-center">
                <Link to="/new-submission">
                  <Button className="bg-[#003594] hover:bg-[#002554] text-white px-8 py-6">
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="px-8 py-6">
                    Go To My Dashboard
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
